// The front door. Split-screen on desktop, single column below `lg`.
//
// LEFT is a showcase and reads as one — marketing copy on our own sign-in page, not a
// readout of anybody's data. Nobody is signed in yet, so there is no data it could show;
// the copy describes what the platform does, never "your" numbers.
//
// Two claims on that panel are load-bearing and were checked against the code rather than
// copied from the brief:
//   - Graph API v24.0, from settings.meta_graph_version. The brief said v23.0.
//   - "Fernet-encrypted", not "256-bit encryption". core/crypto.py derives a 256-bit KEY
//     via sha256, but Fernet is AES-128-CBC + HMAC-SHA256 underneath, so the louder claim
//     would have overstated the cipher on what is presented as a security badge.
//
// RIGHT is the only thing anybody came here to use, so it holds focus: one card, one
// column, nothing decorative competing with the fields.
import { useState, type FormEvent, type ReactNode } from "react";
import { PlatformWordmark } from "@/components/brand/PlatformLogo";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useAuth } from "@/providers/AppProviders";
import { GoogleSignIn } from "@/components/auth/GoogleSignIn";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import {
  TrendingUp,
  Workflow,
  CheckCircle2,
  Lock,
  Shield,
  User,
  Mail,
  Eye,
  EyeOff,
  AlertCircle,
  type LucideIcon,
} from "lucide-react";

interface TokenResponse {
  access_token: string;
}

interface LocationState {
  from?: string;
}

type Mode = "login" | "signup";

interface Capability {
  icon: LucideIcon;
  title: string;
  body: string;
  chips: string[];
}

/** What the left panel says the product does. Copy, not telemetry. */
const CAPABILITIES: Capability[] = [
  {
    icon: TrendingUp,
    title: "Closed-loop attribution",
    body: "Meta ad spend joined to the WhatsApp conversations it started and the revenue that followed — one figure, one source.",
    chips: ["WhatsApp", "Instagram", "Messenger"],
  },
  {
    icon: Workflow,
    title: "Goal-driven agents",
    body: "Qualify a warm lead, answer from your own knowledge, and hand a person the ones that need one.",
    chips: ["Grounded replies", "Human handoff"],
  },
];

const BADGES: Array<{ icon: LucideIcon; label: string }> = [
  { icon: CheckCircle2, label: "Meta Graph API v24.0" },
  { icon: Lock, label: "Fernet-encrypted credentials" },
  { icon: Shield, label: "Per-tenant data isolation" },
];

const INPUT =
  "h-10 w-full rounded-xl border border-brand-border bg-brand-surface pl-9 pr-3 text-[13.5px] text-brand-text-primary outline-none transition-colors placeholder:text-brand-text-secondary/60 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20";

function explainAuthError(err: unknown, mode: Mode): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "That email and password do not match an account.";
    if (err.status === 409) return "An account with that email already exists. Sign in instead.";
    if (err.status === 422) return "Check the email address and try again.";
    if (err.status >= 500) return "We could not reach the workspace. Try again in a moment.";
    return err.message;
  }
  return mode === "signup"
    ? "Could not create the account. Check your connection and try again."
    : "Could not sign in. Check your connection and try again.";
}

/** Label, leading icon and the control, so every row lines up without each caller
 *  repeating the same three wrappers. */
function Field({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[12.5px] font-medium text-brand-text-primary">
        {label}
      </span>
      <span className="relative block">
        <Icon
          size={16}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-brand-text-secondary"
        />
        {children}
      </span>
    </label>
  );
}

export default function Login() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState<Mode>("login");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const destination = (location.state as LocationState | null)?.from ?? "/accounts";

  if (user) return <Navigate to={destination} replace />;

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
    // The password does not survive the switch. It was typed for a different intent, and
    // carrying it means a mistyped sign-in silently becomes a new account's password.
    setPassword("");
  };

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;
    setError(null);
    setNotice(null);
    setIsSubmitting(true);
    try {
      const path = mode === "signup" ? "/api/auth/register" : "/api/auth/login";
      const body =
        mode === "signup"
          ? { email: email.trim(), password, full_name: fullName.trim() || null }
          : { email: email.trim(), password };
      const token = await apiFetch<TokenResponse>(path, { method: "POST", body });
      // signIn stores the token AND confirms it against /me, so a token the server will
      // not honour never reaches a guarded route.
      await signIn(token.access_token, remember);
      setPassword("");
      navigate(destination, { replace: true });
    } catch (err) {
      setError(explainAuthError(err, mode));
      setPassword("");
    } finally {
      setIsSubmitting(false);
    }
  }

  const canSubmit =
    email.trim() !== "" && password !== "" && (mode === "login" || password.length >= 8);

  return (
    <main className="grid min-h-screen grid-cols-1 bg-brand-bg lg:grid-cols-[1.05fr_1fr]">
      {/* ---------------------------------------------------------------- showcase */}
      <section
        aria-label="About ORAI Business Suite"
        className="relative hidden overflow-hidden bg-brand-surface lg:flex lg:flex-col lg:justify-between"
      >
        {/* Two soft radial washes rather than an image: nothing to load, nothing to go
            stale, and it survives a dark-mode swap because both are brand tokens. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.16]"
          style={{
            background:
              "radial-gradient(60% 50% at 18% 12%, var(--brand-primary) 0%, transparent 60%)," +
              "radial-gradient(55% 45% at 88% 88%, var(--brand-primary) 0%, transparent 65%)",
          }}
        />

        <header className="relative flex items-center px-12 pt-10">
          <PlatformWordmark iconClassName="rounded-xl" />
        </header>

        <div className="relative px-12">
          <h1 className="max-w-[13ch] text-[38px] font-semibold leading-[1.08] tracking-[-0.02em] text-brand-text-primary">
            Every rupee of ad spend, followed to the sale.
          </h1>
          <p className="mt-4 max-w-[46ch] text-[14px] leading-relaxed text-brand-text-secondary">
            ORAI joins your Meta campaigns to the conversations they start and the revenue
            they close — and answers the ones you cannot get to.
          </p>

          <div className="mt-8 space-y-3">
            {CAPABILITIES.map((card) => (
              <article
                key={card.title}
                className="rounded-2xl border border-brand-border/70 bg-brand-bg/60 p-4 backdrop-blur-sm"
              >
                <div className="flex items-start gap-3">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-primary/12 text-brand-primary">
                    <card.icon size={18} strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0">
                    <h2 className="text-[13.5px] font-semibold text-brand-text-primary">
                      {card.title}
                    </h2>
                    <p className="mt-1 text-[12.5px] leading-relaxed text-brand-text-secondary">
                      {card.body}
                    </p>
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      {card.chips.map((chip) => (
                        <span
                          key={chip}
                          className="rounded-full border border-brand-border/80 px-2 py-0.5 text-[10.5px] text-brand-text-secondary"
                        >
                          {chip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* Both badges checked against the code, not aspirational. */}
        <footer className="relative flex flex-wrap items-center gap-x-5 gap-y-2 px-12 pb-10 pt-8">
          {BADGES.map((badge) => (
            <span
              key={badge.label}
              className="flex items-center gap-1.5 text-[11.5px] text-brand-text-secondary"
            >
              <badge.icon size={14} className="text-brand-primary shrink-0" />
              {badge.label}
            </span>
          ))}
        </footer>
      </section>

      {/* ------------------------------------------------------------------- form */}
      <section className="flex items-center justify-center px-5 py-10 sm:px-8">
        <div className="w-full max-w-[400px]">
          {/* The brand mark repeats here for single-column viewports, where the showcase
              panel is not rendered at all. */}
          <div className="mb-7 lg:hidden">
            <PlatformWordmark iconClassName="rounded-xl" />
          </div>

          <div
            role="tablist"
            aria-label="Sign in or create an account"
            className="mb-7 grid grid-cols-2 gap-1 rounded-xl bg-brand-surface p-1"
          >
            {(["login", "signup"] as const).map((tab) => (
              <button
                key={tab}
                role="tab"
                type="button"
                aria-selected={mode === tab}
                onClick={() => switchMode(tab)}
                className={cn(
                  "h-9 rounded-lg text-[13px] font-medium transition-colors",
                  mode === tab
                    ? "bg-brand-bg text-brand-text-primary shadow-sm"
                    : "text-brand-text-secondary hover:text-brand-text-primary",
                )}
              >
                {tab === "login" ? "Sign in" : "Create account"}
              </button>
            ))}
          </div>

          <h2 className="text-[22px] font-semibold tracking-[-0.01em] text-brand-text-primary">
            {mode === "login" ? "Welcome back" : "Create your workspace"}
          </h2>
          <p className="mt-1.5 text-[13px] text-brand-text-secondary">
            {mode === "login"
              ? "Enter your credentials to reach your workspace."
              : "Your account becomes the owner of a new workspace."}
          </p>

          {error && (
            <p
              role="alert"
              className="mt-5 flex items-start gap-2 rounded-xl border border-n-critical/40 bg-n-critical/10 px-3 py-2.5 text-[12.5px] leading-relaxed text-brand-text-primary"
            >
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-n-critical" />
              {error}
            </p>
          )}
          {notice && (
            <p
              role="status"
              className="mt-5 rounded-xl border border-brand-primary/30 bg-brand-primary/5 px-3 py-2.5 text-[12.5px] leading-relaxed text-brand-text-secondary"
            >
              {notice}
            </p>
          )}

          <form onSubmit={handleSubmit} className="mt-6 space-y-3.5">
            {mode === "signup" && (
              <Field icon={User} label="Full name">
                <input
                  type="text"
                  value={fullName}
                  autoComplete="name"
                  placeholder="Priya Sharma"
                  onChange={(e) => setFullName(e.target.value)}
                  className={INPUT}
                />
              </Field>
            )}

            <Field icon={Mail} label="Email address">
              <input
                type="email"
                required
                value={email}
                autoComplete="email"
                placeholder="you@company.com"
                onChange={(e) => setEmail(e.target.value)}
                className={INPUT}
              />
            </Field>

            <Field icon={Lock} label="Password">
              <input
                type={showPassword ? "text" : "password"}
                required
                value={password}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
                onChange={(e) => setPassword(e.target.value)}
                className={cn(INPUT, "pr-10")}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-lg text-brand-text-secondary transition-colors hover:text-brand-text-primary"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </Field>

            <div className="flex items-center justify-between gap-3 pt-0.5">
              <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-brand-text-secondary">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="size-3.5 accent-brand-primary"
                />
                Remember this device
              </label>

              {mode === "login" && (
                <button
                  type="button"
                  // Deliberately not a link. There IS no self-serve reset endpoint yet,
                  // and a link to a page that cannot reset anything is worse than saying
                  // so plainly.
                  onClick={() =>
                    setNotice(
                      "Password reset is not self-serve yet. Ask your workspace owner to " +
                        "re-invite you, or contact support to have it reset.",
                    )
                  }
                  className="text-[12.5px] text-brand-primary transition-opacity hover:opacity-80"
                >
                  Forgot password?
                </button>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !canSubmit}
              className="mt-1 w-full"
            >
              {isSubmitting && (
                <span
                  className="size-3 animate-spin rounded-full border-2 border-brand-on-button/30 border-t-brand-on-button"
                  aria-hidden="true"
                />
              )}
              {isSubmitting
                ? "Authenticating workspace…"
                : mode === "login"
                  ? "Sign in to ORAI"
                  : "Create workspace"}
            </Button>

            {mode === "signup" && password !== "" && password.length < 8 && (
              <p className="text-[11.5px] text-brand-text-secondary">
                Use at least 8 characters.
              </p>
            )}
          </form>

          <GoogleSignIn
            onToken={async (accessToken) => {
              await signIn(accessToken, remember);
              navigate(destination, { replace: true });
            }}
            onError={setError}
          />

          <p className="mt-7 flex items-center justify-center gap-1.5 text-[11.5px] text-brand-text-secondary">
            <Lock size={13} />
            Encrypted in transit · your workspace is isolated per tenant
          </p>
        </div>
      </section>
    </main>
  );
}
